import React from 'react';
import './AddPage.scss';
import { Button, Card, Grid, Input, Icon, Checkbox, Dropdown, LabelValue, Textarea, Typography, Modal } from '@design-system/index';
import { AppartementsLine, Wrong } from '@icons';
import { useAddPageVM } from './useAddPageVM';
import { APARTMENT_TYPE, PAYMENT_TYPE } from '@utils/enumTranslation';
import { Formik } from 'formik';
import { BookingToCreate } from '@services/member/booking/booking.types';
import { AsyncDropdown, DropdownOption } from '@design-system/components/Dropdown/AsyncDropdown';
import { MemberTable } from '@services/member/member/member.types';
import { useGetMembersMutation } from '@services/member/member/member.endpoints';
import { formatMoney } from '@utils/numbers';
import PlanningCalendar from '../../DetailsPage/DetailsTabs/DetailsPlanning/PlanningCalendar/PlanningCalendar';

export default function AddPage() {
    const {
        apartment,
        apartmentIsLoading,
        isError,
        isLoading,
        isEdit,
        submitForm,
        booking,
        checkAvailability,
        isAvailable,
        bookingModalOpen,
        setBookingModalOpen,
        apartmentId
    } = useAddPageVM();

    const checkDates = (arrivalDate: string, departureDate: string) => {
        if (!arrivalDate || !departureDate) return true;
        const arrivalDateObject = new Date(arrivalDate);
        const departureDateObject = new Date(departureDate);
        return arrivalDateObject < departureDateObject;
    };

    if (isError) {
        return (
            <Typography intrinsic="p" color="secondary">
                Une erreur est survenue lors de la récupération des informations
            </Typography>
        );
    }

    return (
        <>
            <div className="add-member-container">
                <Card
                    isLoading={isLoading}
                    headerType="full"
                    showBack
                    headerTitle={isEdit ? 'Modification réservation' : 'Création nouvelle réservation'}
                    headerIcon={<Icon icon={<AppartementsLine />} size={20} color="primary" />}
                >
                    <Formik
                        initialValues={
                            booking || {
                                requestDate: new Date().toISOString().split('T')[0],
                                arrivalDate: '',
                                departureDate: '',
                                rentalPrice: '',
                                depositAmount: apartment?.price.deposit ?? '',
                                depositPaymentType: '',
                                cleaningFeeAmount: apartment?.price.cleaningFee ?? '',
                                cleaningFeePaymentType: '',
                                cancellationFeeAmount: apartment?.price.cancellationFee ?? '',
                                cancellationFeePaymentType: '',
                                touristTaxAmount: '',
                                touristTaxPaymentType: '',
                                peopleCount: 2,
                                observation: '',
                                rentalPaymentType: '',
                                badgeNumber: '',
                                keysNumber: '',
                                sharedFirstDay: true,
                                sharedLastDay: true,
                                keysReturned: false
                            }
                        }
                        enableReinitialize
                        onSubmit={async (values: BookingToCreate) => {
                            await submitForm(values);
                        }}
                    >
                        {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => {
                            return (
                                <form onSubmit={handleSubmit} className="form">
                                    <div className="form-section-container">
                                        <div className="form-section" id="apartment-section">
                                            <Typography intrinsic="h3">Logement</Typography>
                                            <div className="form-section-fields">
                                                <Card
                                                    headerType="very-light"
                                                    isLoading={apartmentIsLoading}
                                                    headerTitle={apartment?.fullname}
                                                >
                                                    <Grid columns={3}>
                                                        <LabelValue label="Référence" value={apartment?.reference} />
                                                        <LabelValue label="Intitulé du logement" value={apartment?.label} />
                                                        <LabelValue
                                                            label="Type de logement"
                                                            value={apartment && APARTMENT_TYPE[apartment.type]}
                                                        />
                                                    </Grid>
                                                </Card>
                                                <Card headerType="very-light" headerTitle="Tarifs" isLoading={apartmentIsLoading}>
                                                    <Grid columns={3}>
                                                        {apartment?.apartmentPrices.map((apartmentPrice) => (
                                                            <LabelValue
                                                                key={apartmentPrice.priceType.id}
                                                                label={apartmentPrice.priceType.label}
                                                                value={formatMoney(apartmentPrice.amount)}
                                                            />
                                                        ))}
                                                        <LabelValue label="Caution dégâts" value={formatMoney(apartment?.price.deposit)} />
                                                        <LabelValue
                                                            label="Caution ménage"
                                                            value={formatMoney(apartment?.price.cleaningFee)}
                                                        />
                                                        <LabelValue
                                                            label="Caution annulation"
                                                            value={formatMoney(apartment?.price.cancellationFee)}
                                                        />
                                                    </Grid>
                                                </Card>
                                            </div>
                                        </div>
                                        <div className="form-section" id="apartment-section">
                                            <Typography intrinsic="h3">Locataire</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <AsyncDropdown<MemberTable>
                                                            label="Rechercher un adhérent"
                                                            required
                                                            onOptionChange={(o) => {
                                                                setFieldValue('selectedMember', o);
                                                            }}
                                                            mutationHook={useGetMembersMutation}
                                                            mapDataToOptions={(data) =>
                                                                data.map((item) => ({
                                                                    id: item.id.toString(),
                                                                    label:
                                                                        item.cosemNumber +
                                                                        ' - ' +
                                                                        item.personalInformation.lastname +
                                                                        ' ' +
                                                                        item.personalInformation.firstname
                                                                }))
                                                            }
                                                            value={values.selectedMember as DropdownOption}
                                                            isSearchable
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            type="number"
                                                            required
                                                            name="peopleCount"
                                                            label="Nombre de personnes"
                                                            value={values.peopleCount}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-section" id="booking-section">
                                            <Typography intrinsic="h3">Réservation</Typography>
                                            <div className="form-section-fields">
                                                {!isAvailable && (
                                                    <div className="unavailable-booking-slot">
                                                        <Typography intrinsic="p" variant="p2" color="error">
                                                            <Icon icon={<Wrong />} size={20} color="error" />
                                                            Les dates de réservation ne sont pas disponibles
                                                        </Typography>
                                                    </div>
                                                )}
                                                {!checkDates(values.arrivalDate, values.departureDate) && (
                                                    <div className="unavailable-booking-slot">
                                                        <Typography intrinsic="p" variant="p2" color="error">
                                                            <Icon icon={<Wrong />} size={20} color="error" />
                                                            La date d'arrivée doit être antérieure à la date de départ
                                                        </Typography>
                                                    </div>
                                                )}
                                                <div className="form-group">
                                                    <Button
                                                        variant="secondary"
                                                        onClick={() => {
                                                            setBookingModalOpen(true);
                                                        }}
                                                    >
                                                        Voir le planning
                                                    </Button>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            type="date"
                                                            required
                                                            name="arrivalDate"
                                                            label="Date d'arrivée"
                                                            value={values.arrivalDate}
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                                checkAvailability({ ...values, arrivalDate: e.target.value });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            type="date"
                                                            required
                                                            name="departureDate"
                                                            label="Date de départ"
                                                            value={values.departureDate}
                                                            onChange={(e) => {
                                                                handleChange(e);
                                                                checkAvailability({ ...values, departureDate: e.target.value });
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Checkbox
                                                            checked={!!values.sharedFirstDay}
                                                            onChange={(v) => {
                                                                setFieldValue('sharedFirstDay', v);
                                                                checkAvailability({ ...values, sharedFirstDay: v });
                                                            }}
                                                        >
                                                            <Typography intrinsic="p" variant="p2" color="grey1">
                                                                Premier jour partagé
                                                            </Typography>
                                                        </Checkbox>
                                                    </div>
                                                    <div className="form-group">
                                                        <Checkbox
                                                            checked={!!values.sharedLastDay}
                                                            onChange={(v) => {
                                                                setFieldValue('sharedLastDay', v);
                                                                checkAvailability({ ...values, sharedLastDay: v });
                                                            }}
                                                        >
                                                            <Typography intrinsic="p" variant="p2" color="grey1">
                                                                Dernier jour partagé
                                                            </Typography>
                                                        </Checkbox>
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <Input
                                                                type="number"
                                                                required
                                                                rightContent="€"
                                                                name="rentalPrice"
                                                                label="Prix location"
                                                                value={values.rentalPrice}
                                                                onChange={handleChange}
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <Dropdown
                                                                label="Règlement"
                                                                isClearable
                                                                options={Object.entries(PAYMENT_TYPE).map(([key, value]) => ({
                                                                    id: key,
                                                                    label: value
                                                                }))}
                                                                value={values.rentalPaymentType}
                                                                onOptionChange={(option) => {
                                                                    setFieldValue('rentalPaymentType', option?.id);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <Input
                                                                type="number"
                                                                required
                                                                rightContent="€"
                                                                name="depositAmount"
                                                                label="Caution dégâts"
                                                                value={values.depositAmount}
                                                                onChange={handleChange}
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <Dropdown
                                                                label="Règlement"
                                                                isClearable
                                                                options={Object.entries(PAYMENT_TYPE).map(([key, value]) => ({
                                                                    id: key,
                                                                    label: value
                                                                }))}
                                                                value={values.depositPaymentType}
                                                                onOptionChange={(option) => {
                                                                    setFieldValue('depositPaymentType', option?.id);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-line">
                                                        {apartment?.hasTouristTax && (
                                                            <>
                                                                <div className="form-group">
                                                                    <Input
                                                                        type="number"
                                                                        rightContent="€"
                                                                        name="touristTaxAmount"
                                                                        label="Taxe de séjour"
                                                                        value={values.touristTaxAmount}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>

                                                                <div className="form-group">
                                                                    <Dropdown
                                                                        label="Règlement"
                                                                        isClearable
                                                                        options={Object.entries(PAYMENT_TYPE).map(([key, value]) => ({
                                                                            id: key,
                                                                            label: value
                                                                        }))}
                                                                        value={values.touristTaxPaymentType}
                                                                        onOptionChange={(option) => {
                                                                            setFieldValue('touristTaxPaymentType', option?.id);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>

                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <Input
                                                                type="number"
                                                                required
                                                                rightContent="€"
                                                                name="cleaningFeeAmount"
                                                                label="Caution ménage"
                                                                value={values.cleaningFeeAmount}
                                                                onChange={handleChange}
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <Dropdown
                                                                label="Règlement"
                                                                isClearable
                                                                options={Object.entries(PAYMENT_TYPE).map(([key, value]) => ({
                                                                    id: key,
                                                                    label: value
                                                                }))}
                                                                value={values.cleaningFeePaymentType}
                                                                onOptionChange={(option) => {
                                                                    setFieldValue('cleaningFeePaymentType', option?.id);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <Checkbox
                                                                checked={!!values.keysReturned}
                                                                onChange={(v) => {
                                                                    setFieldValue('keysReturned', v);
                                                                }}
                                                            >
                                                                <Typography intrinsic="p" variant="p2" color="grey1">
                                                                    Clés rendues
                                                                </Typography>
                                                            </Checkbox>
                                                        </div>
                                                    </div>
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <Input
                                                                type="number"
                                                                required
                                                                rightContent="€"
                                                                name="cancellationFeeAmount"
                                                                label="Caution annulation"
                                                                value={values.cancellationFeeAmount}
                                                                onChange={handleChange}
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <Dropdown
                                                                label="Règlement"
                                                                isClearable
                                                                options={Object.entries(PAYMENT_TYPE).map(([key, value]) => ({
                                                                    id: key,
                                                                    label: value
                                                                }))}
                                                                value={values.cancellationFeePaymentType}
                                                                onOptionChange={(option) => {
                                                                    setFieldValue('cancellationFeePaymentType', option?.id);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            type="date"
                                                            required
                                                            name="requestDate"
                                                            label="Demandé le"
                                                            value={values.requestDate}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Textarea
                                                            label="Numéro de badge"
                                                            name="badgeNumber"
                                                            height={100}
                                                            value={values.badgeNumber}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Textarea
                                                            label="Numéro de clés"
                                                            name="keysNumber"
                                                            height={100}
                                                            value={values.keysNumber}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-section" id="observation-section">
                                            <Typography intrinsic="h3">Commentaire</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Textarea
                                                            label="Ajouter un commentaire"
                                                            name="observation"
                                                            value={values.observation}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="action-buttons">
                                        <Button
                                            type="submit"
                                            disabled={isSubmitting || !isAvailable || !checkDates(values.arrivalDate, values.departureDate)}
                                            variant="primary"
                                        >
                                            {isEdit ? 'Valider la modification' : 'Valider la création'}
                                        </Button>
                                    </div>
                                </form>
                            );
                        }}
                    </Formik>
                </Card>
            </div>

            <Modal
                className="planning-modal"
                open={bookingModalOpen}
                yesText="Fermer"
                onValidate={() => {
                    setBookingModalOpen(false);
                }}
                setClose={() => {
                    setBookingModalOpen(false);
                }}
                title="Planning des réservations"
            >
                {apartmentId && <PlanningCalendar apartmentId={apartmentId} hideDetails />}
            </Modal>
        </>
    );
}
